<script setup lang="ts">
	import { onClickOutside, useElementHover } from "@vueuse/core";
	import { ChevronLeftIcon, MenuIcon, XIcon } from "lucide-vue-next";
	import DownloadIcon from "public/images/download.svg";
	import SettingIcon from "public/images/setting.svg";
	import LockIcon from "public/images/lock.svg";
	import LogoutIcon from "public/images/logout.svg";
	import ProfileFilledIcon from "public/images/profile.svg";
	import ProfileIcon from "public/images/profile_chevron.svg";
	import SearchIcon from "public/images/search.svg";
	import TvIcon from "public/images/tv.svg";
	import ReferralIcon from "public/images/invite.svg";
	import TicketIcon from "public/images/ticket.svg";

	import UserManagementIcon from "public/images/user_management.svg";
	import WalletIcon from "public/images/wallet.svg";
	import { ref } from "vue";
	import { useLayoutStore } from "~/store/main";
	import { useAuthStore } from "~/store/auth";
	import { getDaysBetweenDates, toFarsiNumber } from "~/utils";
	import SectionHeaderPinCode from "~/components/sections/sectionHeaderPinCode.vue";

	const { $client } = useNuxtApp();
	const authStore = useAuthStore();
	// const route = useRoute();
	//
	// const showSearch = computed(() => {
	// 	return route.path != "/library";
	// });
	const { menuItems } = useLayoutStore();
	const mobileMenuOpen = ref(false);
	const subscriptionStatus = ref(!!authStore.activeSub);
	const subscriptionType = ref(authStore.activeSub?.name || "-");
	const subscriptionRemaining = ref(
		authStore.activeSub
			? toFarsiNumber(
					getDaysBetweenDates(
						authStore.activeSub.expires_at,
						new Date(),
					),
				)
			: "-",
	);

	const showBottomSheet = ref(false);
	const menuDropdownBtn = ref();
	const menuDropdown = ref();
	const showMenuDropdown = ref(false);
	const showMenuDropdownHover = useElementHover(menuDropdownBtn, {
		delayLeave: 500,
	});
	const showMenuDropdownHover2 = useElementHover(menuDropdown, {
		delayLeave: 500,
	});
	const isUserLogin = ref(false);
	if (useAuthStore().user) {
		isUserLogin.value = true;
	}
	onClickOutside(menuDropdown, () => (showMenuDropdown.value = false));
	const openPopUp = (input: string) => {
		if (input === "mobileMenuOpen") {
			mobileMenuOpen.value = !mobileMenuOpen.value;
			showBottomSheet.value = false;
		} else if (input === "showBottomSheet") {
			showBottomSheet.value = !showBottomSheet.value;
			mobileMenuOpen.value = false;
		}
	};

	const isSubmitting = ref(false);
	const pinCodePopup = ref();
	const showPinCodePopup = ref(false);

	async function enablePinCode() {
		// if (isSubmitting.value) return;
		// isSubmitting.value = true;
		// showPincodePopup.value = true;
		if (authStore.user?.pin) {
			authStore.lockPin();
			showPinCodePopup.value = false;
			isSubmitting.value = false;
		} else {
			// setTimeout(() => {
			pinCodePopup.value.setPin(() => {
				// console.log("das");
				// showPincodePopup.value = false;
				// isSubmitting.value = false;
			});
			// }, 500);
		}
		authStore.lockPin();
	}

	async function disablePinCode() {
		// if (isSubmitting.value) return;
		// isSubmitting.value = true;
		// showPincodePopup.value = true;
		// setTimeout(() => {
		pinCodePopup.value.checkPin(() => {
			// console.log("das");
			// showPincodePopup.value = false;
			// isSubmitting.value = false;
		});
		// }, 500);
	}

	const logout = async () => {
		isSubmitting.value = true;
		try {
			const { success } = await $client.rpcAuth.logout.query();
			if (success) {
				authStore.logout();
				isUserLogin.value = false;
				showBottomSheet.value = false;
				navigateTo("/");
			} else {
				isSubmitting.value = false;
				console.error("Logout Error");
			}
		} catch (e) {
			isSubmitting.value = false;
			console.error(e);
		}
	};
</script>

<template>
	<div class="w-full">
		<section-header-pin-code ref="pinCodePopup" />
		<!-- Sticky Navigation -->
		<nav
			class="flex sticky top-0 flex-col items-center justify-center z-10 backdrop-blur-sm"
		>
			<div class="w-full bg-rev1White/95 px-6 sm:px-6 lg:px-20">
				<div class="flex items-center justify-between h-16">
					<div class="flex items-center">
						<!-- Mobile navigation: menu -->
						<div class="flex md:hidden">
							<button
								class="inline-flex items-center justify-center p-2 rounded-md text-dark hover:text-primary hover:bg-rev1White focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
								@click="openPopUp('mobileMenuOpen')"
							>
								<MenuIcon
									v-if="!mobileMenuOpen"
									class="block h-6 w-6"
								/>
								<XIcon v-else class="block h-6 w-6" />
							</button>
						</div>

						<!-- Logo -->
						<NuxtLink
							to="/"
							class="block flex-shrink-0 mx-2 cursor-pointer"
						>
							<img
								src="/images/multicolor_sedna_world_logo.svg"
								alt="Logo"
								class="h-8"
							/>
						</NuxtLink>

						<!-- Desktop navigation: menu -->
						<div class="hidden md:block">
							<div class="flex flex-row gap-6 ms-10">
								<NuxtLink
									v-for="(item, index) in menuItems"
									:key="index"
									:to="item.link"
									class="outline-black rounded-full hover:text-primary py-2 text-md font-normal cursor-pointer"
								>
									{{ item.title }}
								</NuxtLink>
							</div>
						</div>
					</div>

					<!-- Mobile navigation: search and profile -->
					<div
						class="md:hidden flex gap-2 flex-row items-center justify-between"
					>
						<NuxtLink to="/search">
							<button
								class="h-10 w-10 justify-center items-center outline-black outline-1 outline-offset-2 transition-colors p-1 rounded-full text-dark hover:text-primary"
							>
								<SearchIcon
									class="scale-[0.85] stroke-dark hover:stroke-primary cursor-pointer"
								/>
							</button>
						</NuxtLink>
						<button
							v-show="isUserLogin"
							class="outline-black h-10 w-16 justify-center items-center flex outline-1 outline-offset-2 transition-colors p-1 rounded-full text-dark hover:text-primary"
							@click="openPopUp('showBottomSheet')"
						>
							<ProfileIcon
								class="scale-[0.85] stroke-dark hover:stroke-primary cursor-pointer"
							></ProfileIcon>
						</button>
						<!--						<button-->
						<!--							v-show="isUserLogin"-->
						<!--							class="p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"-->
						<!--							@click="openPopUp('showBottomSheet')"-->
						<!--						>-->
						<!--							<ProfileIcon-->
						<!--								class="stroke-dark hover:stroke-primary h-6 w-6 cursor-pointer"-->
						<!--							></ProfileIcon>-->
						<!--						</button>-->
						<button
							v-show="!isUserLogin"
							class="min-h-0 h-btn btn px-4 py-2 text-white border-0 text-md font-normal transition-colors bg-primary rounded-full hover:bg-primaryHover"
							@click="navigateTo('/login')"
						>
							ورود | ثبت نام
						</button>
					</div>

					<!-- Desktop navigation: search and profile -->
					<div class="hidden md:block">
						<div class="flex items-center gap-2">
							<NuxtLink to="/search">
								<button
									class="h-10 w-10 justify-center items-center outline-black outline-1 outline-offset-2 transition-colors p-1 rounded-full text-dark hover:text-primary"
								>
									<SearchIcon
										class="scale-[0.85] stroke-dark hover:stroke-primary cursor-pointer"
									/>
								</button>
							</NuxtLink>
							<NuxtLink to="/subscription">
								<button
									v-if="!subscriptionStatus"
									class="outline-black px-3 rounded-full py-2 text-dark text-md font-normal border-transparent transition-colors hover:text-primary"
								>
									خرید اشتراک
								</button>
							</NuxtLink>
							<div v-if="isUserLogin">
								<button
									class="landscapeHeader:hidden flex outline-black h-10 w-16 justify-center items-center outline-1 outline-offset-2 transition-colors p-1 rounded-full text-dark hover:text-primary"
									@click="showBottomSheet = !showBottomSheet"
								>
									<ProfileIcon
										class="scale-[0.85] stroke-dark hover:stroke-primary cursor-pointer"
									></ProfileIcon>
								</button>
								<button
									class="landscapeHeader:flex hidden outline-black h-10 w-16 justify-center items-center outline-1 outline-offset-2 transition-colors p-1 rounded-full text-dark hover:text-primary"
									@click="
										showMenuDropdown = !showMenuDropdown
									"
								>
									<ProfileIcon
										class="scale-[0.85] stroke-dark hover:stroke-primary cursor-pointer"
									></ProfileIcon>
								</button>
								<div
									v-show="
										showMenuDropdown ||
										showMenuDropdownHover ||
										showMenuDropdownHover2
									"
									ref="menuDropdown"
									class="absolute min-w-80 z-10 bg-rev1White top-16 left-10 rounded-2xl right-auto text-start text-sm font-medium mx-auto p-4 flex flex-col justify-center shadow shadow-stone-700"
								>
									<div
										class="flex flex-col justify-center items-center text-dark"
									>
										<div
											v-show="!subscriptionStatus"
											class="m-4 flex flex-row gap-2 text-errorRed"
										>
											<div class="relative">
												<TicketIcon
													class="stroke-errorRed"
												></TicketIcon>
											</div>
											اشتراک فعال ندارید
										</div>
										<NuxtLink
											to="/subscription"
											@click="
												showMenuDropdownHover = false
											"
										>
											<button
												v-show="!subscriptionStatus"
												class="min-h-0 h-btn btn px-4 py-2 text-white border-0 font-medium transition-colors bg-primary rounded-full hover:bg-primaryHover"
											>
												<span>خرید اشتراک</span>
												<ChevronLeftIcon
													class="w-5 h-5"
												/>
											</button>
										</NuxtLink>
										<div
											v-show="subscriptionStatus"
											class="w-full justify-between flex flex-row"
										>
											<h1>
												اشتراک {{ subscriptionType }}
											</h1>

											<h1 class="text-grey50">
												باقی مانده:
												<span class="text-dark"
													>{{
														toFarsiNumber(
															subscriptionRemaining,
														)
													}}
													روز</span
												>
											</h1>
										</div>
										<div
											class="w-full border-t border-dark mt-4 mb-4"
										/>
										<div class="w-full flex flex-col gap-4">
											<NuxtLink
												to="/panel/profile"
												@click="
													showMenuDropdownHover = false
												"
											>
												<div
													class="whitespace-nowrap px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												>
													<ProfileFilledIcon
														class="fill-dark"
													></ProfileFilledIcon>
													{{
														authStore?.user
															? `${authStore?.user?.name} ${authStore?.user?.surname}`
															: ""
													}}
												</div>
											</NuxtLink>
											<NuxtLink
												to="/panel/accounts"
												@click="
													showMenuDropdownHover = false
												"
											>
												<div
													class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												>
													<UserManagementIcon></UserManagementIcon>
													تغییر تماشا کننده (
													{{
														authStore?.userAccount
															? `${authStore?.userAccount?.name} ${authStore?.userAccount?.surname}`
															: ""
													}}
													)
												</div>
											</NuxtLink>
										</div>
										<div
											class="w-full border-t border-dark my-4"
										/>

										<div class="w-full flex flex-col gap-4">
											<NuxtLink
												class="w-full"
												to="/panel/wallet"
												@click="
													showMenuDropdownHover = false
												"
											>
												<div
													class="px-2 w-full justify-between flex items-center flex-row h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												>
													<div
														class="flex items-center flex-row gap-2"
													>
														<WalletIcon
															class="stroke-dark"
														></WalletIcon>
														کیف پول
													</div>
													<div
														class="flex items-center flex-row gap-2"
													>
														<span>
															{{
																toFarsiNumber(
																	authStore.coins,
																)
															}}
														</span>
														<img
															alt="coin"
															src="/images/coins.svg"
															class="fill-black w-5 h-5"
														/>
													</div>
												</div>
											</NuxtLink>
											<NuxtLink
												class="w-full"
												to="/panel/downloads"
												@click="
													showMenuDropdownHover = false
												"
											>
												<div
													class="px-2 w-full justify-between flex items-center flex-row h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												>
													<div
														class="flex items-center flex-row gap-2"
													>
														<DownloadIcon
															class="stroke-dark"
														></DownloadIcon>
														خرید دانلود
													</div>
													<div
														class="flex items-center flex-row gap-2"
													>
														<span
															class="text-gray-400"
														>
															باقی مانده:<a
																class="text-dark"
															>
																{{
																	toFarsiNumber(
																		authStore
																			?.user
																			?.available_download_token,
																	)
																}}
															</a>
														</span>
													</div>
												</div>
											</NuxtLink>
										</div>

										<div
											class="w-full border-t border-dark my-4"
										/>

										<div class="w-full flex flex-col gap-4">
											<NuxtLink
												class="w-full"
												to="/subscription"
												@click="
													showMenuDropdownHover = false
												"
											>
												<div
													class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												>
													<img
														alt="subscription"
														src="/images/ticket_black.svg"
														class="fill-dark w-5 h-5 stroke-dark"
													/>
													خرید اشتراک
												</div>
											</NuxtLink>
											<div
												class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												@click="
													authStore.pinUnlocked()
														? enablePinCode()
														: disablePinCode()
												"
											>
												<LockIcon
													viewBox="0 0 23 23"
													class="stroke-dark"
												></LockIcon>
												قفل کودک
												<a
													v-if="
														authStore.pinUnlocked()
													"
													class="bg-infoBlue px-3 py-1 rounded-full text-sm text-white"
													>فعال کن</a
												>
												<a
													v-else
													class="bg-errorRed px-3 py-1 rounded-full text-sm text-white"
													>غیر فعال کن</a
												>
											</div>
											<NuxtLink
												class="w-full"
												to="/panel"
												@click="showBottomSheet = false"
											>
												<div
													class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												>
													<SettingIcon
														class="stroke-dark"
													></SettingIcon>
													<!--												<img-->
													<!--													alt="setting"-->
													<!--													src="/images/setting.svg"-->
													<!--													class="fill-black w-5 h-5"-->
													<!--												/>-->
													حساب کاربری
												</div>
											</NuxtLink>
											<!--											<div-->
											<!--												class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"-->
											<!--											>-->
											<!--												<ListIcon-->
											<!--													class="stroke-dark"-->
											<!--												></ListIcon>-->
											<!--												لیست‌های من-->
											<!--											</div>-->
											<NuxtLink
												to="/activate"
												class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												@click="showBottomSheet = false"
											>
												<TvIcon
													class="stroke-dark"
												></TvIcon>
												فعال سازی در تلویزیون هوشمند
											</NuxtLink>
											<NuxtLink
												class="w-full"
												to="/referral"
												@click="showBottomSheet = false"
											>
												<div
													class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												>
													<ReferralIcon
														viewBox="0 0 24 24"
														class="stroke-dark w-5 h-5 flex justify-center items-center"
													></ReferralIcon>
													دعوت از دوستان
													<span
														class="text-infoOrange"
														>سکه بگیر!</span
													>
													<img
														alt="invite"
														src="/images/referral_smiley.svg"
														class="fill-black w-8 h-8"
													/>
												</div>
											</NuxtLink>
											<div
												class="text-errorRed px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
												@click="logout()"
											>
												<LogoutIcon
													class="stroke-errorRed"
												></LogoutIcon>
												خروج از حساب کاربری
											</div>
										</div>
									</div>
								</div>
							</div>
							<button
								v-else
								class="outline-black outline-offset-2 btn min-h-0 h-btn px-4 py-2 text-white border-0 transition-colors bg-primary rounded-full hover:bg-primaryHover"
								@click="navigateTo('/login')"
							>
								ورود | ثبت نام
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Mobile menu, show/hide based on menu state. -->
			<div
				v-show="mobileMenuOpen"
				class="absolute shadow-[0_1px_0_0_rgba(0,0,0,0.1)] rounded-b-2xl top-16 md:hidden overflow-hidden w-full bg-rev1White/95 backdrop-blur-sm"
			>
				<div class="w-full px-2 pt-2 pb-3 space-y-1 sm:px-3">
					<div class="flex flex-col">
						<NuxtLink
							v-for="(item, index) in menuItems"
							:key="index"
							:to="item.link"
							class="hover:text-primary block px-3 py-4 rounded-md font-medium text-sm"
							@click="mobileMenuOpen = false"
						>
							{{ item.title }}
						</NuxtLink>
					</div>
				</div>
				<NuxtLink to="/subscription" @click="mobileMenuOpen = false">
					<div class="pt-4 pb-3 border-t border-gray-700">
						<div class="flex items-center px-5">
							<a
								class="cursor-pointer text-md font-normal border-transparent transition-colors hover:text-primary"
							>
								خرید اشتراک
							</a>
						</div>
					</div>
				</NuxtLink>
			</div>
		</nav>

		<!-- Mobile profile menu -->
		<layout-bottom-sheet
			class="z-[999] h-full"
			:v-if="showBottomSheet"
			:is-show="showBottomSheet"
			:is-visible="showBottomSheet"
			@close="showBottomSheet = false"
		>
			<div class="z-50 mx-4">
				<div
					class="z-50 pb-4 flex flex-col justify-center items-center text-dark"
				>
					<div
						v-show="!subscriptionStatus"
						class="my-2 flex flex-row gap-2 text-errorRed"
					>
						<div class="relative">
							<TicketIcon class="stroke-errorRed"></TicketIcon>
						</div>
						اشتراک فعال ندارید
					</div>
					<NuxtLink
						v-show="!subscriptionStatus"
						to="/subscription"
						@click="showBottomSheet = false"
					>
						<button
							class="min-h-0 h-btn btn px-4 py-2 text-white border-0 font-medium transition-colors bg-primary rounded-full hover:bg-primaryHover"
						>
							<span>خرید اشتراک</span>
							<ChevronLeftIcon class="w-5 h-5" />
						</button>
					</NuxtLink>
					<div
						v-show="subscriptionStatus"
						class="w-full mt-10 justify-between flex flex-row"
					>
						<h1>اشتراک {{ subscriptionType }}</h1>
						<h1 class="text-grey50">
							باقی مانده:
							<span class="text-dark"
								>{{
									toFarsiNumber(subscriptionRemaining)
								}}
								روز</span
							>
						</h1>
					</div>

					<div class="w-full border-t border-dark mt-4 mb-4" />

					<div class="w-full flex flex-col gap-4">
						<NuxtLink
							to="/panel/profile"
							@click="showBottomSheet = false"
						>
							<div
								class="whitespace-nowrap px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
							>
								<ProfileFilledIcon
									class="fill-dark"
								></ProfileFilledIcon>
								{{
									authStore?.user
										? `${authStore?.user?.name} ${authStore?.user?.surname}`
										: ""
								}}
							</div>
						</NuxtLink>
						<NuxtLink
							to="/panel/accounts"
							@click="showBottomSheet = false"
						>
							<div
								class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
							>
								<UserManagementIcon
									viewBox="0 0 24 24"
									class="stroke-dark w-5 h-5 flex justify-center items-center"
								></UserManagementIcon>
								تغییر تماشا کننده (
								{{
									authStore?.userAccount
										? `${authStore?.userAccount?.name} ${authStore?.userAccount?.surname}`
										: ""
								}}
								)
							</div>
						</NuxtLink>
					</div>
					<div class="w-full border-t border-dark my-4" />

					<div class="w-full flex flex-col gap-4">
						<NuxtLink
							class="w-full"
							to="/panel/wallet"
							@click="showBottomSheet = false"
						>
							<div
								class="px-2 w-full justify-between flex items-center flex-row h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
							>
								<div class="flex items-center flex-row gap-2">
									<WalletIcon
										viewBox="0 0 24 24"
										class="stroke-dark w-5 h-5 flex justify-center items-center"
									></WalletIcon>
									کیف پول
								</div>
								<div class="flex items-center flex-row gap-2">
									<span>
										{{ toFarsiNumber(authStore.coins) }}
									</span>
									<img
										alt="coin"
										src="/images/coins.svg"
										class="fill-black w-5 h-5"
									/>
								</div>
							</div>
						</NuxtLink>
						<NuxtLink
							class="w-full"
							to="/panel/downloads"
							@click="showBottomSheet = false"
						>
							<div
								class="px-2 w-full justify-between flex items-center flex-row h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
							>
								<div class="flex items-center flex-row gap-2">
									<DownloadIcon
										viewBox="0 0 24 24"
										class="stroke-dark w-5 h-5 flex justify-center items-center"
									></DownloadIcon>
									خرید دانلود
								</div>
								<div class="flex items-center flex-row gap-2">
									<span class="text-gray-400">
										باقی مانده:<a class="text-dark">
											{{
												toFarsiNumber(
													authStore?.user
														?.available_download_token,
												)
											}}
										</a>
									</span>
								</div>
							</div>
						</NuxtLink>
					</div>
					<div class="w-full border-t border-dark my-4" />
					<div class="w-full flex flex-col gap-4">
						<NuxtLink
							class="w-full"
							to="/subscription"
							@click="showBottomSheet = false"
						>
							<div
								class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
							>
								<img
									alt="subscription"
									src="/images/ticket_black.svg"
									class="fill-dark w-5 h-5 stroke-dark"
								/>
								خرید اشتراک
							</div>
						</NuxtLink>
						<div
							class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
							@click="
								authStore.pinUnlocked()
									? enablePinCode()
									: disablePinCode()
							"
						>
							<LockIcon
								viewBox="0 0 24 24"
								class="stroke-dark w-5 h-5 flex justify-center items-center"
							></LockIcon>
							قفل کودک
							<a
								v-if="authStore.pinUnlocked()"
								class="bg-infoBlue px-3 py-1 rounded-full text-sm text-white"
								>فعال کن</a
							>
							<a
								v-else
								class="bg-errorRed px-3 py-1 rounded-full text-sm text-white"
								>غیر فعال کن</a
							>
						</div>
						<NuxtLink
							class="w-full"
							to="/panel"
							@click="showBottomSheet = false"
						>
							<div
								class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
							>
								<img
									alt="setting"
									src="/images/setting.svg"
									class="fill-black w-5 h-5"
								/>
								حساب کاربری
							</div>
						</NuxtLink>
						<!--						<div-->
						<!--							class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"-->
						<!--						>-->
						<!--							<ListIcon-->
						<!--								viewBox="0 0 24 24"-->
						<!--								class="stroke-dark w-5 h-5 flex justify-center items-center"-->
						<!--							></ListIcon>-->
						<!--							لیست‌های من-->
						<!--						</div>-->
						<NuxtLink
							to="/activate"
							class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
						>
							<TvIcon
								viewBox="0 0 24 24"
								class="stroke-dark w-5 h-5 flex justify-center items-center"
							></TvIcon>
							فعال سازی در تلویزیون هوشمند
						</NuxtLink>
						<NuxtLink
							class="w-full"
							to="/referral"
							@click="showBottomSheet = false"
						>
							<div
								class="px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
							>
								<ReferralIcon
									viewBox="0 0 24 24"
									class="stroke-dark w-5 h-5 flex justify-center items-center"
								></ReferralIcon>

								دعوت از دوستان
								<span class="text-infoOrange">سکه بگیر!</span>
								<img
									alt="invite"
									src="/images/referral_smiley.svg"
									class="fill-black w-8 h-8"
								/>
							</div>
						</NuxtLink>
						<div
							class="text-errorRed px-2 w-full flex items-center flex-row gap-2 h-8 cursor-pointer hover:bg-gray-200 hover:rounded-md"
							@click="logout()"
						>
							<LogoutIcon
								viewBox="0 0 24 24"
								class="stroke-errorRed w-5 h-5 flex justify-center items-center"
							></LogoutIcon>
							خروج از حساب کاربری
						</div>
					</div>
				</div>
			</div>
		</layout-bottom-sheet>

		<!-- Stop scroll behaviour on menu open -->
		<div v-if="showBottomSheet" class="no-doc-scroll absolute" />
	</div>
</template>
